import {
  Chip,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import { Icons } from 'utoniq-ui'

import {
  ItemDescriptionFragment,
  UserItemSerialDetailFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { BrowserLink } from '@/components/lib/BrowserLink'
import { Loading } from '@/components/organisms/loading'
import { TokenId } from '@/components/organisms/nft'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { getHelpUrl } from '@/lib/helpUrl'

export * from './fragments'

export type State = {
  loading: boolean
  item: ItemDescriptionFragment | null
  serials: Array<UserItemSerialDetailFragment> | null
}

export type Action = {}

export const NftItemsTemplate: Template<State, Action> = ({ state }) => {
  const { t } = useTranslation('nftItems')
  const { router, generatePath, routerPages, routerNames } = useRouter()
  const { item, serials } = state

  return (
    <>
      <Head title={routerNames.OwnedItemNftItems} />
      <BaseLayout
        header={{
          title: 'NFT',
          back: true,
        }}
      >
        {state.loading ? (
          <LoadingCell />
        ) : (
          <Stack flex={1} direction="column" sx={{ pt: 2, px: 5 }}>
            <HeadingTextCell />
            <LinkCell />
            <SubHeadingTextCell />

            {serials?.length ? (
              serials.map(serial => {
                return (
                  <ListItem
                    key={serial.id}
                    divider
                    sx={{ py: 3 }}
                    disablePadding
                    secondaryAction={
                      <ActionButtonCell
                        isNft={!!serial.isNft}
                        processingItemRegisterer={!serial.tokenId}
                      />
                    }
                    disableGutters={true}
                  >
                    <ListItemButton
                      component={Stack}
                      disabled={!serial.tokenId}
                      onClick={() =>
                        router.push(
                          generatePath(routerPages.OwnedItemNftItemDetail, {
                            itemId: item?.id,
                            serialId: serial.id,
                          })
                        )
                      }
                    >
                      <ListItemText
                        sx={{ flex: '0 0 auto' }}
                        primary={serial.formattedSerialNumber}
                        secondary={
                          <TokenId
                            tokenId={serial.tokenId}
                            variant="subtitle2"
                            withTitle
                          />
                        }
                        slotProps={{
                          primary: { variant: 'h6' },
                          secondary: {
                            variant: 'subtitle2',
                            color: 'text.secondary',
                          },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                )
              })
            ) : (
              <Typography variant="body1" sx={{ p: 4, textAlign: 'center' }}>
                {t('serialNotFound')}
              </Typography>
            )}
          </Stack>
        )}
      </BaseLayout>
    </>
  )
}

const LoadingCell: Cell = () => {
  return (
    <CellFrame sx={{ height: '100%', display: 'grid', placeItems: 'center' }}>
      <Loading />
    </CellFrame>
  )
}

const HeadingTextCell: Cell = () => {
  const { t } = useTranslation('nftItems')
  return (
    <CellFrame>
      <Stack direction="row" sx={{ py: 1 }}>
        <Typography variant="h5">{t('issuableNft')}</Typography>
        <Typography variant="subtitle1" alignSelf="end">
          {t('free')}
        </Typography>
      </Stack>
    </CellFrame>
  )
}

const LinkCell: Cell = () => {
  const { t } = useTranslation('nftItems')
  return (
    <BrowserLink
      component={Stack}
      href={getHelpUrl('howToIssueNft')}
      sx={{ py: 1 }}
    >
      <Stack direction="row" alignItems="center">
        <Typography variant="caption" color="primary.main" sx={{ mr: 1 }}>
          {t('usageLink')}
        </Typography>
        <Icons.Help sx={{ color: 'primary.main', height: 12, width: 12 }} />
      </Stack>
    </BrowserLink>
  )
}

const SubHeadingTextCell: Cell = () => {
  const { t } = useTranslation('nftItems')
  return (
    <CellFrame>
      <Typography variant="body2" sx={{ pt: 5, pb: 1 }}>
        {t('selectSerialNumber')}
      </Typography>
    </CellFrame>
  )
}

const ActionButtonCell: Cell<{
  isNft: boolean
  processingItemRegisterer: boolean
}> = ({ isNft, processingItemRegisterer }) => {
  const { t } = useTranslation('nftItems')

  const StatusLabel = ({
    color,
    bgcolor,
    text,
  }: {
    color: string
    bgcolor: string
    text: string
  }) => {
    return (
      <Chip
        label={
          <Typography variant="subtitle2" color={color}>
            {text}
          </Typography>
        }
        sx={{
          bgcolor,
          borderRadius: '2px',
          minWidth: 84,
          height: 24,
        }}
      />
    )
  }

  if (processingItemRegisterer)
    return (
      <StatusLabel
        color="common.white"
        bgcolor="secondary.dark"
        text={t('issuing')}
      />
    )

  if (isNft)
    return (
      <StatusLabel
        color="common.white"
        bgcolor="secondary.main"
        text={t('issuedNft')}
      />
    )

  return (
    <StatusLabel
      color="text.secondary"
      bgcolor="grey.100"
      text={t('notIssuedNft')}
    />
  )
}
